import { useEffect, useMemo, useState } from 'react';
import logo from './TT_LOGO.png';
import './GolfPayment.css';

const money = cents => `R${(cents / 100).toFixed(2)}`;

function ClubPage({ clubName, children }) {
  return <main className="Club-pay">
    <div className="Club-corner Club-corner--top" aria-hidden="true" />
    <div className="Club-corner Club-corner--bottom" aria-hidden="true" />
    <div className="Club-shell">
      <header className="Club-header">
        <img className="Club-logo" src={logo} alt="TipTag — tap tip go" width="1300" height="775" />
        {clubName && <h1>{clubName}</h1>}
      </header>
      <div className="Club-panel">{children}</div>
    </div>
  </main>;
}

export default function GolfPayment({ tagId, fallback, renderPayment }) {
  const [config, setConfig] = useState({state:'loading'});
  const [retry, setRetry] = useState(0);
  const [selectedPayType, setPayType] = useState(null);
  const [holes, setHoles] = useState(null);
  const [amount, setAmount] = useState('');
  const [includeCharges, setIncludeCharges] = useState(false);
  const [result, setResult] = useState(null);
  const [paymentActive, setPaymentActive] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setConfig({state:'loading'});
    fetch(`/api/club.php?tagId=${encodeURIComponent(tagId)}`)
      .then(async response => {
        if (!response.ok) throw new Error();
        const data = await response.json();
        if (!Object.prototype.hasOwnProperty.call(data, 'club')) throw new Error();
        if (!cancelled) setConfig({state:'ready',club:data.club});
      }).catch(() => { if (!cancelled) setConfig({state:'error'}); });
    return () => { cancelled = true; };
  }, [tagId, retry]);

  const club = config.club;
  const scannedCaddie = club?.scannedCaddie;
  const caddieId = scannedCaddie?.id || '';
  const payType = selectedPayType;
  const context = useMemo(() => ({clubId:club?.id, payType,
    ...(payType === 'round' ? {holes} : {caddieId,baseAmountCents:Math.round(Number(amount)*100),includeCharges})
  }), [club, payType, holes, caddieId, amount, includeCharges]);
  const selectionKey = JSON.stringify([tagId, context]);
  const valid = Boolean(club) && Boolean(payType) && (payType === 'round' ? [9,18].includes(holes)
    : Boolean(caddieId) && /^\d+(\.\d{1,2})?$/.test(amount) && Number(amount) >= 1 && Number(amount) <= 1000000);

  useEffect(() => {
    if (!valid) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      fetch('/api/club.php', {method:'POST',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({tagId,paymentContext:context})})
        .then(async response => {
          if (!response.ok) throw new Error();
          const quote = await response.json();
          if (!Number.isSafeInteger(quote.amountCents) || quote.amountCents <= 0 || !Number.isSafeInteger(quote.feeCents)
            || !Number.isSafeInteger(quote.baseAmountCents) || quote.baseAmountCents <= 0) throw new Error();
          if (!cancelled) setResult({key:selectionKey,quote});
        }).catch(() => { if (!cancelled) setResult({key:selectionKey,error:true}); });
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [valid, tagId, context, selectionKey, retry]);

  if (config.state === 'loading') return <ClubPage><div className="Club-state" role="status"><span className="Club-loader" aria-hidden="true" />Loading club…</div></ClubPage>;
  if (config.state === 'error') return <ClubPage><div className="Club-state"><p role="alert">Club payments are temporarily unavailable.</p><button className="Club-control Club-retry" onClick={()=>setRetry(retry+1)}>Try again</button></div></ClubPage>;
  if (!club) return fallback;

  if (!payType) return <ClubPage clubName={club.name}>
    <section className="Club-landing" aria-labelledby="club-payment-options">
      <h2 id="club-payment-options">What would you like to do?</h2>
      <div className="Club-landing-options">
        <button className="Club-control" type="button" onClick={()=>setPayType('round')}>Pay round fee</button>
        <button className="Club-control" type="button" onClick={()=>setPayType('tip')}>Leave a tip</button>
      </div>
    </section>
  </ClubPage>;

  const current = valid && result?.key === selectionKey ? result : null;
  return <ClubPage clubName={club.name}>
    <fieldset className="Club-selection" disabled={paymentActive}>
    <legend className="Club-sr-only">Payment selection</legend>
    <button className="Club-control Club-back" type="button" onClick={()=>setPayType(null)}>
      <span aria-hidden="true">← </span>Back to payment options
    </button>
    {payType === 'round' ? <section aria-label="Choose a round">
      <h2>Choose your round</h2>
      <div className="Club-options Club-rounds">
        {club.rounds.map(round => <button className="Club-control" type="button" key={round.holes} aria-pressed={holes === round.holes} onClick={()=>setHoles(round.holes)}>
          <span>{round.holes} holes</span>
          <strong>{money(round.amountCents)}</strong>
        </button>)}
      </div>
      <p className="Club-note">A 6.33% transaction fee is added to the round price.</p>
    </section> : <section aria-label="Caddie tip">
      <h2>Thank your caddie</h2>
      {club.caddies.length === 0 ? <p>No caddies are available for tipping at this club yet.</p> : <>
        {scannedCaddie && <p className="Club-recipient">Tipping <strong>{scannedCaddie.name}</strong></p>}
        <div className="Club-tip-amounts">
          <div className="Club-presets" role="group" aria-label="Suggested tip amounts">
            {['10','15','20','50','100','200'].map(v=><button className="Club-control" type="button" key={v} aria-pressed={amount === v} onClick={()=>setAmount(v)}>R{v}</button>)}
          </div>
        </div>
        <label htmlFor="club-tip">Tip amount (rand)</label>
        <div className="Club-amount-field"><span aria-hidden="true">R</span><input id="club-tip" type="number" inputMode="decimal" min="1" max="1000000" step="0.01" placeholder="Enter your amount" value={amount} onChange={e=>setAmount(e.target.value)} aria-describedby="club-tip-help" /></div>
        <p id="club-tip-help" className="Club-note">Enter R1 to R1,000,000, with up to two decimal places.</p>
        <div className="Club-fee-option">
          <label className="Club-cover"><input type="checkbox" checked={includeCharges} onChange={e=>setIncludeCharges(e.target.checked)} aria-describedby="club-fee-help" /><span>Cover the 6.33% transaction fee</span></label>
          <p id="club-fee-help" className="Club-note">{includeCharges ? 'The total increases so your caddie receives your full selected tip.' : 'The transaction fee is deducted from your tip.'}</p>
        </div>
      </>}
    </section>}
    </fieldset>
    <div className="Club-checkout">
    {!valid && <p className="Club-next">{payType === 'round' ? 'Select a round to see your total.'
      : scannedCaddie ? 'Choose your tip amount to continue.' : "Open your caddie's personal TipTag link to send them a tip."}</p>}
    {paymentActive && <p className="Club-notice" role="status">Finish or cancel your payment before changing this selection.</p>}
    {valid && !current && <p className="Club-notice" role="status">Calculating total…</p>}
    {current?.error && <div className="Club-notice"><p role="alert">The payment total is unavailable. Please try again.</p><button className="Club-control Club-retry" onClick={()=>setRetry(retry+1)}>Try again</button></div>}
    {current?.quote && <>
      <dl className="Club-summary" aria-live="polite">
        <dt>Recipient</dt><dd>{current.quote.recipient}</dd>
        <dt>{payType === 'round' ? 'Round price' : 'Tip amount'}</dt><dd>{money(current.quote.baseAmountCents)}</dd>
        <dt>{payType === 'round' || includeCharges ? 'Transaction fee (added)' : 'Transaction fee (included)'}</dt><dd>{money(current.quote.feeCents)}</dd>
        <dt className="Club-total-label">Total to pay</dt><dd className="Club-total"><strong>{money(current.quote.amountCents)}</strong></dd>
      </dl>
      {renderPayment(current.quote, context, setPaymentActive)}
    </>}
    </div>
  </ClubPage>;
}
